import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { AnnouncementModal } from '@/components/announcements/announcement-modal'
import { ResolutionModal } from '@/components/governance/resolution-modal'
import { useAuth } from '@/hooks/use-auth'
import { useDeleteResolution, useResolution } from '@/hooks/use-resolutions'
import { formatDate } from '@/lib/utils'
import { htmlToExcerpt } from '@riverpark/utils/html'
import { isCommitteeRole } from '@riverpark/types'

export function ResolutionDetailPage({ basePath }: { basePath: string }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading } = useResolution(id)
  const deleteResolution = useDeleteResolution()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)

  const resolution = data?.data
  const canManage = user ? isCommitteeRole(user.role) : false

  const backLink = (
    <Link
      to={basePath}
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to governance
    </Link>
  )

  if (isLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      </PageShell>
    )
  }

  if (!resolution) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">Resolution not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  async function handleDelete() {
    if (!resolution) return
    await deleteResolution.mutateAsync(resolution.id)
    navigate(basePath)
  }

  return (
    <PageShell>
      <PageHeader
        label="Governance"
        title={resolution.title}
        description={`${resolution.authorName}${
          resolution.passedAt ? ` · Passed ${formatDate(new Date(resolution.passedAt * 1000))}` : ''
        }`}
        action={backLink}
      />

      <div className="max-w-2xl space-y-4">
        {resolution.pollTitle && (
          <p className="font-sans text-xs uppercase tracking-wide text-muted-foreground">
            From poll: {resolution.pollTitle}
          </p>
        )}
        <div
          className="font-sans text-sm leading-relaxed text-ink [&_a]:text-primary [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: resolution.body }}
        />

        {canManage && (
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setShowEditModal(true)}>
              Edit
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowAnnouncementModal(true)}>
              Publish as announcement
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} className="mr-1.5" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {showEditModal && (
        <ResolutionModal resolution={resolution} onClose={() => setShowEditModal(false)} />
      )}

      {showAnnouncementModal && (
        <AnnouncementModal
          prefill={{
            title: `Resolution: ${resolution.title}`,
            body: `<p>A new resolution has been published: "${resolution.title}".</p><p>${htmlToExcerpt(resolution.body, 200)}</p>`,
            category: 'governance',
            link: { entityType: 'resolution', entityId: resolution.id },
          }}
          onClose={() => setShowAnnouncementModal(false)}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/40 p-4">
          <div className="w-full max-w-sm rounded-card border border-border-2 bg-card p-6">
            <h2 className="font-serif text-lg text-ink">Delete resolution?</h2>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              "{resolution.title}" will be permanently removed. This can't be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={deleteResolution.isPending}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  )
}
